export const SIGNS = [
    'aries',
    'taurus',
    'gemini',
    'cancer',
    'leo',
    'virgo',
    'libra',
    'scorpio',
    'sagittarius',
    'capricorn',
    'aquarius',
    'pisces',
] as const;
export type SignId = (typeof SIGNS)[number];

/** U+FE0E asks for the plain text form of a symbol: many of these glyphs default to coloured emoji. */
const TEXT = '\uFE0E';

/** Glyphs for the signs, in the order of SIGNS. */
export const SIGN_GLYPHS = '♈♉♊♋♌♍♎♏♐♑♒♓'
    .split('')
    .map((g) => g + TEXT);

export const BODIES = [
    'sun',
    'moon',
    'mercury',
    'venus',
    'mars',
    'jupiter',
    'saturn',
    'uranus',
    'neptune',
    'pluto',
    'node',
    'chiron',
] as const;
export type BodyId = (typeof BODIES)[number];

const BODY_TEXT_GLYPHS: Record<BodyId, string> = {
    sun: '☉',
    moon: '☽',
    mercury: '☿',
    venus: '♀',
    mars: '♂',
    jupiter: '♃',
    saturn: '♄',
    uranus: '♅',
    neptune: '♆',
    pluto: '♇',
    node: '☊',
    chiron: '⚷',
};

export const BODY_GLYPHS = Object.fromEntries(
    Object.entries(BODY_TEXT_GLYPHS).map(([id, g]) => [id, g + TEXT])
) as Record<BodyId, string>;

/** The modern ruler of each sign, which is the planet that carries the chart's thread when the sign rises. */
export const RULERS: Record<SignId, BodyId> = {
    aries: 'mars',
    taurus: 'venus',
    gemini: 'mercury',
    cancer: 'moon',
    leo: 'sun',
    virgo: 'mercury',
    libra: 'venus',
    scorpio: 'pluto',
    sagittarius: 'jupiter',
    capricorn: 'saturn',
    aquarius: 'uranus',
    pisces: 'neptune',
};

/** Degrees folded into [0, 360). */
export const norm360 = (deg: number): number => ((deg % 360) + 360) % 360;

export const signIndexOf = (lon: number): number =>
    Math.floor(norm360(lon) / 30) % 12;

export const signOf = (lon: number): SignId => SIGNS[signIndexOf(lon)];

/** Whole degrees and minutes inside the sign, truncated so a position never reads 30°00'. */
export const inSign = (lon: number): { degree: number; minute: number } => {
    const total = Math.floor((norm360(lon) % 30) * 60 + 1e-9);
    return { degree: Math.floor(total / 60), minute: total % 60 };
};
